import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { GridColumnVisibilityModel, GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../../../common/routesUtils";
import ServerTable from "../../../../../../components/common/table/ServerTable";
import { useServerTableController } from "../../../../../../components/common/table/ServerTable/ServerDataGrid/useServerTableController";
import { CustomColumnMenu } from "../../../../../../components/common/table/TableData/CustomColumnMenu";
import { CustomDataGridFooter } from "../../../../../../components/common/table/TableData/TableDataFooter";
import TableSelectActions from "../../../../../../components/common/table/TableData/TableSelectActions";
import { useCustomTableDataStyles } from "../../../../../../components/common/table/TableData/useCustomTableDataStyles";
import { PageType } from "../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../../../context/FiltersProvider";
import { usePageConfigurationState } from "../../../../../../context/PageConfigrations/usePageConfigurationState";
import { TripsPageDTO } from "../../../../../../datasources/api-client/data-contracts";
import { makeSnackbarMessage, publishSnackbarMessage } from "../../../../../../state/view";
import { useCurrentTheme } from "../../../../../../theme/theme";
import { GreenrouterRow } from "../../../../ResultsOperationalUnits/OperationalUnitsGreenrouter/data";
import CustomNoRowsOverlay from "../CustomNoRowsOverlay";
import { getColumns, getRows } from "./data";

interface Props {
	data: TripsPageDTO;
	isLoading: boolean;
	reload: () => void;
}

const TableTransportGreenrouter: FC<Props> = ({ data, isLoading, reload }) => {
	const { t } = useTranslation(["common", "result.transport"]);
	const { visitPage } = useBreadcrumbs();
	const navigate = useNavigate();
	const publishMessage = useSetAtom(publishSnackbarMessage);
	const { theme } = useCurrentTheme();
	const tableSx = useCustomTableDataStyles();

	const { appliedFilters } = useFiltersRegistry();

	// Page configuration stored in localStorage
	const [config, setConfig] = usePageConfigurationState(PageType.ResultsTransportGreenRouter, {
		emissionsKey: "co2Ttw",
		page: 0,
		pageSize: 10,
		sortField: undefined,
		sortDirection: undefined,
		columnVisibilityModel: {},

		entityPage: 0,
		entityEmissionsKey: "co2Ttw",
		entityColumnVisibilityModel: {},
	});

	// Generic table controller
	const {
		paginationModel,
		handlePaginationChange,
		sortModel,
		handleSortModelChange,
		columnVisibilityModel,
		handleColumnVisibilityChange,
	} = useServerTableController({
		config,
		setConfig,
	});

	// Build table rows only when the API data changes
	const rows = useMemo(() => (data ? getRows(data) : []), [data]);

	// Use the first item to resolve dynamic column measures
	const dataSample = data?.items?.[0];

	// Keep navigation callback stable
	const navigateToTransport = useCallback(
		(id: string, code?: string) => {
			visitPage(PageType.ResultsTransportGreenRouterDetail, false, id, code ?? id);
			navigate(AppRoutes.ResultsTransportGreenrouterDetail.replace(":id", id));
		},
		[visitPage, navigate]
	);

	const textPaletteColor = theme.palette.primary.contrastText;

	// Resolve the emissions column selected from the page switch
	const activeEmissionsField = useMemo(
		() => (config.emissionsKey === "co2Ttw" ? "tCo2eTtw" : "tCo2eWtw"),
		[config.emissionsKey]
	);

	// Remove the inactive emissions column from the grid definition
	const columns = useMemo(() => {
		const allColumns = getColumns(textPaletteColor, t, navigateToTransport, dataSample);

		return allColumns.filter((column) => {
			const isEmissionsColumn = column.field === "tCo2eTtw" || column.field === "tCo2eWtw";

			return !isEmissionsColumn || column.field === activeEmissionsField;
		});
	}, [textPaletteColor, t, navigateToTransport, dataSample, activeEmissionsField]);

	// Set the default visibility only for columns currently available
	const defaultColumnVisibility = useMemo<GridColumnVisibilityModel>(() => {
		return columns.reduce<GridColumnVisibilityModel>((acc, column) => {
			acc[column.field] = true;
			return acc;
		}, {});
	}, [columns]);

	// Apply the persisted user visibility over the defaults
	const mergedVisibilityModel = useMemo<GridColumnVisibilityModel>(
		() => ({
			...defaultColumnVisibility,
			...columnVisibilityModel,
		}),
		[defaultColumnVisibility, columnVisibilityModel]
	);

	// delete
	// const { put: deleteNode, isLoading: deleteNodeLoading } = useWriteResource({
	// 	route: ApiRoutes.ConfigurationDeleteNodes,
	// });

	const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
		type: "include",
		ids: new Set<GridRowId>(),
	});

	// Create a stable dependency for applied filters
	const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

	// Reset selection and pagination when filters change
	useEffect(() => {
		setSelectedRowIds({
			type: "include",
			ids: new Set(),
		});

		if (config.page !== 0) {
			setConfig({ page: 0 });
		}
	}, [appliedFiltersKey]);

	const handleRowSelection = (newSelection: GridRowSelectionModel) => {
		setSelectedRowIds({
			type: newSelection.type,
			ids: new Set(newSelection.ids),
		});
	};

	// Resolve selected IDs for both include and exclude selection modes
	const selectedRowsId: GridRowId[] = useMemo(() => {
		if (selectedRowIds.type === "include") {
			return Array.from(selectedRowIds.ids);
		}

		return rows
			.filter((row: GreenrouterRow) => !selectedRowIds.ids.has(row.id))
			.map((row: GreenrouterRow) => row.id);
	}, [selectedRowIds, rows]);

	const handleDeleteRows = async () => {
		try {
			// await deleteNode({ ids: selectedRowsId });
			console.log("DELETE", selectedRowsId);

			setSelectedRowIds({
				type: "include",
				ids: new Set(),
			});

			publishMessage(makeSnackbarMessage(t("common:delete rows dialog/success message"), "success"));
		} catch {
			publishMessage(
				makeSnackbarMessage(t("common:delete rows dialog/the deletion could not be completed"), "error")
			);
		}
	};

	if (isLoading) {
		return <Skeleton variant="rounded" height="100%" width="100%" />;
	}

	return (
		<ServerTable
			title={t("result.transport:list/greenrouter transports")}
			isLoading={isLoading}
			onReload={reload}
			actions={
				data?.itemsCount ? (
					<TableSelectActions
						selectedRowsId={selectedRowsId}
						onDeleteRows={handleDeleteRows} // TODO update query
						deleteLoading={false} // TODO update query
					/>
				) : null
			}
			getRowId={(row) => row.id}
			rows={rows}
			columns={columns}
			rowHeight={56}
			checkboxSelection
			rowSelectionModel={selectedRowIds}
			onRowSelectionModelChange={handleRowSelection}
			paginationModel={paginationModel}
			onPaginationModelChange={handlePaginationChange}
			rowCount={data?.itemsCount ?? 0}
			pageSizeOptions={[10, 25, 50]}
			sortModel={sortModel}
			onSortModelChange={handleSortModelChange}
			columnVisibilityModel={mergedVisibilityModel}
			onColumnVisibilityModelChange={handleColumnVisibilityChange}
			slots={{
				noRowsOverlay: () => <CustomNoRowsOverlay />,
				footer: CustomDataGridFooter,
				columnMenu: CustomColumnMenu,
			}}
			sx={tableSx}
		/>
	);
};

export default TableTransportGreenrouter;
